/**
 * client/net/projectiles.ts — own-projectile prediction [NET §8.3 "Client"].
 * OWNER: NETCODE agent.
 *
 * - When the local prediction first produces PEv.WEAPON_FIRE / WEAPON_FIRE_LAST (crossbow bolt,
 *   ballistic knife blade) or PEv.OFFHAND_THROW (hatchet), a local visual missile is launched with
 *   the SAME shared code as the server (computeMissileLaunch + missileStep from
 *   shared/weapons/missile.ts), tagged (me, ps.fireSeq), colliding with the static world AND the
 *   other players at their interpolated poses, using the server's own hit volumes
 *   (sim/combat/hitvolumes.ts traceHitVolumes, the same trace as CombatSystem.combatTrace; the
 *   30x30x70 movement box predicted hits the server then rejected).
 *   Deviation from NET §8.3 ("static world only"): with world-only collision every hit showed the
 *   bolt ~200 ms (RTT + interp) stuck in the wall BEHIND the victim before the server copy jumped
 *   into the victim. The server rewinds players to the shooter's view for the whole flight
 *   (PROJECTILE_ANTILAG "full", §8.3), i.e. exactly these interpolated poses, so a predicted player
 *   hit agrees with the server. The local bolt then sticks to that player (same offset frame as a
 *   STUCK_PLAYER server missile, §7.4) and is shown as STUCK_PLAYER. Damage is still never predicted.
 *   The server tags its missile with the owner's ps.fireSeq after the same cmd, so they match.
 * - While the local one exists, the server copy with the same (owner, fireSeq, kind) is hidden.
 * - When the server copy (newest snapshot) is no longer FLYING (stuck / pickup), the local one moves
 *   to the server's resting transform: blended over 100 ms if it is within 16 units, else snapped;
 *   then it is dropped and the server copy shows (once its interpolated state is at rest too).
 * - If the server never confirms within 1 s (fire rejected / mispredicted), the local one is dropped.
 * Hits, damage and pickups are never predicted [NET §7.3].
 */
import { CONTENTS, SURFACE_TYPE_FLESH, type CollisionWorld, type TraceFn } from '@shared/collision/types';
import { traceHitVolumes, type PlayerPose, type VolumeHit } from '@sim/combat/hitvolumes';
import { CMD_HZ, MAX_CLIENTS } from '@shared/constants';
import { stuckOffsetToWorld, worldToStuckOffset } from '@shared/protocol/entities';
import { lerpAngle, vclone, vcopy, vdistance, vectorToAngles, vlerp, type Vec3 } from '@shared/math';
import { computeMissileLaunch, missileStep } from '@shared/weapons/missile';
import type { MissileSim, MissileStepResult } from '@shared/weapons/types';
import {
  MissileKind, MissileState, PEv, WeaponId,
  type MissileEntityState, type PlayerState,
} from '@shared/types';
import type { RenderMissile } from '../types';
import type { ParentPose } from './interp';

/** NET §8.3: blend to the server's resting transform over 100 ms when closer than 16 units. */
export const HANDOVER_BLEND_MS = 100;
export const HANDOVER_SNAP_DIST = 16;
/** NET §8.3: drop an unconfirmed local projectile after 1 s. */
export const UNCONFIRMED_TIMEOUT_MS = 1000;
/** Local flight is stepped at the cmd rate (1/60 s), like the server's catch-up sub-steps. */
const STEP_MS = 1000 / CMD_HZ;
/** A local missile that keeps flying with no server copy is dropped after the weapon's lifetime cap (ESTIMATE). */
const MAX_LOCAL_LIFETIME_MS = 30000;

interface LocalMissile {
  id: number;
  owner: number;
  fireSeq: number;
  kind: MissileKind;
  sim: MissileSim;
  firedAt: number; //          real ms
  simTime: number; //          cmd-timeline ms reached by the local sim
  accum: number;
  resting: boolean;
  matched: boolean;
  /** Handover blend: from -> server resting transform. */
  blendStart: number;
  from: Vec3;
  fromAngles: Vec3;
  done: boolean;
  /** Player (clientNum) the local missile hit and sticks to; -1 = none. */
  parent: number;
  /** Offset in the parent's frame (stuckOffsetToWorld) once known; relYaw = missile yaw minus parent yaw at impact. */
  offsetSet: boolean;
  offset: Vec3;
  relYaw: number;
}

const PROJECTILE_FIRE_WEAPONS = new Set<number>([WeaponId.CROSSBOW, WeaponId.KNIFE_BALLISTIC]);

export class ProjectilePredictor {
  private readonly locals: LocalMissile[] = [];
  private nextId = -1;
  private readonly trace: TraceFn;
  private readonly vh: VolumeHit = { frac: 0, normal: [0, 0, 0], point: [0, 0, 0], loc: 0 };
  private readonly stepOut: MissileStepResult = { hit: false, entityNum: 0, pos: [0, 0, 0], normal: [0, 0, 0], surfaceType: 0, bounced: false };
  /** Counters (debug / tests). */
  launched = 0;
  confirmed = 0;
  expired = 0;

  /** `getTargets`: other players' interpolated poses (what the shooter sees = what the server rewinds to). */
  constructor(world: CollisionWorld, getTargets: () => readonly PlayerPose[] = () => []) {
    const vh = this.vh;
    this.trace = (out, start, end, mins, maxs, mask, pass) => {
      world.trace(out, start, end, mins, maxs, mask, pass);
      if (out.allsolid || !(mask & CONTENTS.BODY)) return out;
      let best = out.fraction, who = -1;
      for (const pose of getTargets()) {
        if (pose.clientNum === pass || !pose.alive) continue;
        if (traceHitVolumes(start as Vec3, end as Vec3, pose, vh) && vh.frac < best) { // read-only use
          best = vh.frac;
          who = pose.clientNum;
          out.normal[0] = vh.normal[0]; out.normal[1] = vh.normal[1]; out.normal[2] = vh.normal[2];
        }
      }
      if (who >= 0) { // mirrors CombatSystem.combatTrace
        out.fraction = best;
        out.startsolid = false;
        out.entityNum = who;
        out.contents = CONTENTS.BODY;
        out.surfaceType = SURFACE_TYPE_FLESH;
        out.surfaceFlags = SURFACE_TYPE_FLESH << 20;
        out.endpos[0] = start[0] + (end[0] - start[0]) * best;
        out.endpos[1] = start[1] + (end[1] - start[1]) * best;
        out.endpos[2] = start[2] + (end[2] - start[2]) * best;
      }
      return out;
    };
  }

  get count(): number { return this.locals.length; }

  /** Hook for Predictor.onFreshEvent: launch on the first prediction of a fire/throw event. */
  onPredictedEvent(ps: Readonly<PlayerState>, ev: number, parm: number, realNow: number): void {
    let weapon: WeaponId;
    if ((ev === PEv.WEAPON_FIRE || ev === PEv.WEAPON_FIRE_LAST) && PROJECTILE_FIRE_WEAPONS.has(parm & 0xff)) weapon = (parm & 0xff) as WeaponId;
    else if (ev === PEv.OFFHAND_THROW) weapon = ((parm & 0xff) || WeaponId.HATCHET) as WeaponId;
    else return;
    let launch;
    try { launch = computeMissileLaunch(ps as PlayerState, weapon); } catch { return; }
    const sim: MissileSim = {
      kind: launch.kind, weapon: launch.weapon, owner: ps.clientNum, origin: vclone(launch.origin), velocity: vclone(launch.velocity),
      angles: vclone(launch.angles), gravity: launch.gravity, state: MissileState.FLYING, spawnTime: ps.commandTime,
    };
    this.locals.push({
      id: this.nextId--, owner: ps.clientNum, fireSeq: ps.fireSeq, kind: launch.kind, sim, firedAt: realNow, simTime: ps.commandTime,
      accum: 0, resting: false, matched: false, blendStart: -1, from: [0, 0, 0], fromAngles: [0, 0, 0], done: false,
      parent: -1, offsetSet: false, offset: [0, 0, 0], relYaw: 0,
    });
    this.launched++;
  }

  /**
   * Advance local flights, match against the newest snapshot (`latest`) and the interpolated server
   * missiles (`interp`), and rewrite `interp` in place: hidden server copies removed, local ones added.
   */
  update(
    realNow: number, dtMs: number, latest: readonly MissileEntityState[], interp: RenderMissile[],
    resolveServerPos: (m: MissileEntityState, outPos: Vec3, outAngles: Vec3) => void, parentPose?: ParentPose,
  ): void {
    const tmpPos: Vec3 = [0, 0, 0], tmpAng: Vec3 = [0, 0, 0];
    for (const l of this.locals) {
      if (!l.resting) this.fly(l, dtMs);
      if (l.parent >= 0 && l.blendStart < 0 && parentPose) this.followParent(l, parentPose, tmpPos);
      const srv = latest.find((m) => m.owner === l.owner && m.fireSeq === l.fireSeq && m.kind === l.kind);
      if (srv) { if (!l.matched) this.confirmed++; l.matched = true; }
      if (!l.matched) {
        if (realNow - l.firedAt > UNCONFIRMED_TIMEOUT_MS) { l.done = true; this.expired++; }
        continue;
      }
      if (!srv) { l.done = true; continue; } // server copy gone (exploded / picked up)
      if (srv.state === MissileState.FLYING) {
        if (realNow - l.firedAt > MAX_LOCAL_LIFETIME_MS) l.done = true;
        continue;
      }
      // server copy at rest: hand over
      resolveServerPos(srv, tmpPos, tmpAng);
      if (l.blendStart < 0) {
        l.resting = true;
        if (vdistance(l.sim.origin, tmpPos) < HANDOVER_SNAP_DIST) {
          l.blendStart = realNow;
          vcopy(l.from, l.sim.origin);
          vcopy(l.fromAngles, l.sim.angles);
        } else l.blendStart = realNow - HANDOVER_BLEND_MS; // snap
      }
      const f = Math.min(1, (realNow - l.blendStart) / HANDOVER_BLEND_MS);
      if (f < 1) {
        vlerp(l.sim.origin, l.from, tmpPos, f);
        for (let i = 0; i < 3; i++) l.sim.angles[i] = lerpAngle(l.fromAngles[i], tmpAng[i], f);
      } else {
        vcopy(l.sim.origin, tmpPos);
        vcopy(l.sim.angles, tmpAng);
        const shown = interp.find((m) => m.entId === srv.entId);
        if (!shown || shown.state !== MissileState.FLYING) l.done = true; // interpolated copy is at rest too
      }
    }
    for (let i = this.locals.length - 1; i >= 0; i--) if (this.locals[i].done) this.locals.splice(i, 1);
    // hide server copies of live local missiles, then add the locals
    for (let i = interp.length - 1; i >= 0; i--) {
      const m = interp[i];
      if (this.locals.some((l) => l.owner === m.owner && l.kind === m.kind && this.matchesEnt(l, m.entId, latest))) interp.splice(i, 1);
    }
    for (const l of this.locals) {
      interp.push({
        entId: l.id, kind: l.kind, owner: l.owner,
        state: !l.resting ? MissileState.FLYING : l.parent >= 0 ? MissileState.STUCK_PLAYER : MissileState.STUCK_WORLD,
        origin: vclone(l.sim.origin), angles: vclone(l.sim.angles), velocity: vclone(l.sim.velocity), explodeAt: 0, predicted: true,
      });
    }
  }

  clear(): void { this.locals.length = 0; }

  /** Keep a local missile that hit a player on that player's interpolated body (NET §7.4 frame). */
  private followParent(l: LocalMissile, parentPose: ParentPose, tmp: Vec3): void {
    const yaw = parentPose(l.parent, tmp);
    if (yaw === null) return; // parent not drawn (died / left): stay where it is
    if (!l.offsetSet) {
      worldToStuckOffset(l.offset, l.sim.origin, tmp, yaw);
      l.relYaw = l.sim.angles[1] - yaw;
      l.offsetSet = true;
    }
    stuckOffsetToWorld(l.sim.origin, l.offset, tmp, yaw);
    l.sim.angles[1] = l.relYaw + yaw;
  }

  private matchesEnt(l: LocalMissile, entId: number, latest: readonly MissileEntityState[]): boolean {
    const srv = latest.find((m) => m.entId === entId);
    return !!srv && srv.fireSeq === l.fireSeq;
  }

  private fly(l: LocalMissile, dtMs: number): void {
    l.accum += Math.min(dtMs, 250);
    while (l.accum >= STEP_MS && !l.resting) {
      l.accum -= STEP_MS;
      l.simTime += STEP_MS;
      const r = missileStep(l.sim, STEP_MS, this.trace, l.simTime, this.stepOut);
      if (r.hit && !r.bounced) {
        l.resting = true; // stuck in the world or a player (visual only; the server decides what really happened)
        if (r.entityNum >= 0 && r.entityNum < MAX_CLIENTS) l.parent = r.entityNum;
        l.sim.velocity[0] = l.sim.velocity[1] = l.sim.velocity[2] = 0;
      } else if (!r.bounced && l.kind !== MissileKind.HATCHET) {
        // bolts and blades fly nose-first (the hatchet's spin is the weapons code's business)
        vectorToAngles(l.sim.angles, l.sim.velocity);
      }
    }
  }
}
